import React, { memo, useEffect } from "react";
import { Card, CardBody, Row, Col, Button, UncontrolledAlert, ButtonToolbar, Spinner } from "reactstrap";
import { reduxForm, Field } from "redux-form";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { MdArrowBack } from "react-icons/md";
import { Link } from "react-router-dom";
import validate from "./validate";
import DomainDropDown from "../../../Domains/DomainDropDown";
import SCPIssuersDropDown from "../../../Issuers/SCPIssuersDropDown";
import { resetPost } from "../../actions/issuerDomainMap.actions";
import { renderField } from "../../../../utils/renderfield";

const IssuerDomainMapForm = memo((props) => {
    const { handleSubmit, dispatch, reset, pristine, invalid, submitting, mapping, fetchData, domainCode, createIssuerDomainMapping, change } = props;

    const foundMapping = mapping && mapping.response && mapping.response.data && mapping.response.data.length && !mapping.loading;

    const editMode = mapping && mapping.response && mapping.response.data && mapping.response.data.length && mapping.loading;

    useEffect(() => {
        if (domainCode && !editMode) {
            if (mapping && mapping.response && mapping.response.data && mapping.response.data.length) {
                dispatch(change("domain", mapping.response.data[0].domainCode));
                dispatch(change("issuer", mapping.response.data[0].issuerName));
            }
        }
    }, [domainCode, editMode]);

    return (
        <Col md={12} lg={12}>
            <Card>
                <CardBody>
                    <div className="card__title">
                        <h5 className="bold-text">
                            <Link to="/issuer-mapping" id="link-all-mappings">
                                <MdArrowBack size={20} /> Back to mappings
                            </Link>
                        </h5>
                    </div>
                    {domainCode && mapping && mapping.loading ? (
                        <div className="text-center">
                            <Spinner color="success" className="my-4" style={{ width: "6rem", height: "6rem" }} />
                            <h4 className="text-secondary">Fetching Issuer Mapping for domain code ({domainCode})</h4>
                        </div>
                    ) : (
                        <div>
                            {domainCode && !foundMapping ? (
                                <>
                                    <h4 className="text-danger">{mapping && mapping.error ? `Something went wrong. Could not fetch issuer mapping for domain code (${domainCode})` : `Issuer mapping for (${domainCode}) not found`}</h4>
                                    <Button color="primary" id="btn-try-again" outline size="sm" onClick={fetchData}>
                                        Try Again
                                    </Button>
                                </>
                            ) : (
                                <form className="form" onSubmit={handleSubmit}>
                                    {createIssuerDomainMapping && createIssuerDomainMapping.error && createIssuerDomainMapping.error.errors && createIssuerDomainMapping.error.errors.length ? (
                                        <UncontrolledAlert color="danger">
                                            <h5 className="font-weight-bold">Please check the following fields for errors</h5>
                                            {createIssuerDomainMapping.error.errors.map((err) => (
                                                <p>
                                                    <strong>{err.fieldName}:</strong> {err.message}
                                                </p>
                                            ))}
                                        </UncontrolledAlert>
                                    ) : null}

                                    <Row>
                                        <Col lg="4">
                                            {domainCode && foundMapping ? (
                                                <div className="form__form-group">
                                                    <span className="form__form-group-label ">Domain</span>
                                                    <div className="form__form-group-field">
                                                        <Field id="domains" name="domains" component={renderField} type="text" disabled={true} />
                                                    </div>
                                                </div>
                                            ) : (
                                                <DomainDropDown disabled={!!domainCode} id="domain" label="Domains" />
                                            )}
                                        </Col>
                                        <Col lg="4">
                                            {domainCode && foundMapping ? (
                                                <div className="form__form-group">
                                                    <span className="form__form-group-label ">Issuer</span>
                                                    <div className="form__form-group-field">
                                                        <Field id="issuer" name="issuer" component={renderField} type="text" disabled={true} />
                                                    </div>
                                                </div>
                                            ) : (
                                                <SCPIssuersDropDown id="issuer" required label="Select Issuer" isScpLite={true}/>
                                            )}
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col lg="4">
                                            <div className="form__form-group">
                                                <span className="form__form-group-label required">Bank Code</span>
                                                <div className="form__form-group-field">
                                                    <Field id="bankCode" name="bankCode" component={renderField} type="text" placeholder="Bank Code" />
                                                </div>
                                            </div>
                                        </Col>
                                        <Col lg="4">
                                            <div className="form__form-group">
                                                <span className="form__form-group-label required">Issuer Number</span>
                                                <div className="form__form-group-field">
                                                    <Field id="issuerNr" name="issuerNr" component={renderField} type="text" placeholder="Issuer Number" />
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col lg="4">
                                            <div className="form__form-group">
                                                <span className="form__form-group-label required">Report Issuer</span>
                                                <div className="form__form-group-field">
                                                    <Field id="reportPrefix" name="reportPrefix" component={renderField} type="text" placeholder="Report Issuer" />
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                    <ButtonToolbar className="form__button-toolbar">
                                        {domainCode && foundMapping ? null : (
                                            <Button type="button" id="btn-reset-mapping" onClick={reset} disabled={pristine || submitting}>
                                            Reset
                                            </Button>
                                        )}
                                        <Button color="primary" id="btn-submit-mapping" type="submit" disabled={submitting || invalid}>
                                            {createIssuerDomainMapping && createIssuerDomainMapping.loading ? (
                                                <span>
                                                    <Spinner size="sm" color="default" />{" "}
                                                </span>
                                            ) : null}
                                            Submit
                                        </Button>
                                    </ButtonToolbar>
                                </form>
                            )}
                        </div>
                    )}
                </CardBody>
            </Card>
        </Col>
    );
});

IssuerDomainMapForm.propTypes = {
    handleSubmit: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
};

export default reduxForm({
    form: "issuerDomainMapping_form",
    validate,
    enableReinitialize: true,
})(
    connect((state) => ({
        createIssuerDomainMapping: state.createIssuerDomainMapping,
    }))(IssuerDomainMapForm)
);
